/* ============================================
   BREW HOUSE — Core Theme JavaScript
   ============================================ */

(function () {
  'use strict';

  /* --- Utility Functions --- */
  const BH = window.BrewHouse = window.BrewHouse || {};

  BH.debounce = function (fn, delay = 150) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  BH.throttle = function (fn, limit = 100) {
    let inThrottle = false;
    return function (...args) {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  BH.fetchJSON = async function (url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...options.headers,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[BrewHouse] Fetch error:', error);
      throw error;
    }
  };

  BH.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (parseInt(cents, 10) / 100).toFixed(2);
    const moneyFormat = format || window.Shopify?.money_format || '₹{{amount}}';
    return moneyFormat.replace(/\{\{amount\}\}/g, value)
      .replace(/\{\{amount_no_decimals\}\}/g, Math.round(value))
      .replace(/\{\{amount_with_comma_separator\}\}/g, value.replace('.', ','));
  };

  BH.escapeHtml = function (text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  BH.trapFocus = function (container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    first.focus();
  };

  /* --- Scroll Direction Detection (Header Hide/Show) --- */
  BH.initScrollDetection = function () {
    let lastScrollY = window.scrollY;
    let ticking = false;
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const threshold = 5;
    const headerHeight = header.offsetHeight;

    function updateHeader() {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollY;

      if (currentScrollY <= headerHeight) {
        /* At top — always show */
        header.classList.remove('header--hidden');
        header.classList.remove('header--shrunk');
        header.classList.add('header--at-top');
      } else if (diff > threshold) {
        /* Scrolling down — hide */
        header.classList.add('header--hidden');
        header.classList.add('header--shrunk');
        header.classList.remove('header--at-top');
      } else if (diff < -threshold) {
        /* Scrolling up — show but shrunk */
        header.classList.remove('header--hidden');
        header.classList.add('header--shrunk');
        header.classList.remove('header--at-top');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });
  };

  /* --- AJAX Cart API --- */
  BH.Cart = {
    async get() {
      return BH.fetchJSON('/cart.js');
    },

    async add(items) {
      const data = await BH.fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async update(updates) {
      const data = await BH.fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async change(id, quantity) {
      const data = await BH.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id, quantity }),
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    async clear() {
      const data = await BH.fetchJSON('/cart/clear.js', {
        method: 'POST',
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return data;
    },

    updateCartCount(count) {
      const badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach((badge) => {
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      });
    },
  };

  /* --- Cart Drawer --- */
  BH.CartDrawer = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-overlay]');
      if (!this.drawer) return;

      /* Open triggers */
      document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      /* Close triggers */
      document.querySelectorAll('[data-cart-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      /* ESC key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      /* Listen for cart updates */
      document.addEventListener('cart:updated', () => this.refresh());
    },

    isOpen() {
      return this.drawer?.classList.contains('cart-drawer--open');
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('cart-drawer--open');
      this.overlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      BH.trapFocus(this.drawer);
      this.refresh();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('cart-drawer--open');
      this.overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    },

    async refresh() {
      try {
        const cart = await BH.Cart.get();
        BH.Cart.updateCartCount(cart.item_count);
        this.render(cart);
      } catch (err) {
        console.error('[BrewHouse] Cart refresh error:', err);
      }
    },

    render(cart) {
      const container = this.drawer?.querySelector('[data-cart-items]');
      if (!container) return;

      if (cart.item_count === 0) {
        container.innerHTML = this.renderEmpty();
        return;
      }

      const freeShippingThreshold = 99900; // ₹999.00 in cents
      const currentTotal = cart.total_price;
      const amountLeft = freeShippingThreshold - currentTotal;
      let shippingMessage = '';
      let progressPercent = Math.min((currentTotal / freeShippingThreshold) * 100, 100);

      if (amountLeft > 0) {
        shippingMessage = `You're <strong>${BH.formatMoney(amountLeft)}</strong> away from free shipping!`;
      } else {
        shippingMessage = `<strong>You've unlocked free shipping!</strong> 🎉`;
      }

      container.innerHTML = `
        <div class="cart-drawer__shipping">
          <p class="text-sm">${shippingMessage}</p>
          <div class="shipping-progress">
            <div class="shipping-progress__bar" style="width: ${progressPercent}%"></div>
          </div>
        </div>
        <div class="cart-drawer__items">
          ${cart.items.map((item) => this.renderItem(item)).join('')}
        </div>
        <div class="cart-drawer__footer">
          <div class="cart-drawer__subtotal">
            <span class="text-uppercase letter-spaced text-sm">Subtotal</span>
            <span class="text-uppercase letter-spaced">${BH.formatMoney(cart.total_price)}</span>
          </div>
          <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
          <button class="btn btn--secondary btn--full mt-sm" onclick="BrewHouse.CartDrawer.close()">Continue Shopping</button>
        </div>
      `;

      this.bindItemEvents();
    },

    renderItem(item) {
      return `
        <div class="cart-item" data-cart-item data-variant-id="${item.variant_id}">
          <div class="cart-item__image">
            <img src="${item.image ? item.image.replace(/(\.[^.]+)$/, '_180x$1') : ''}" alt="${item.title}" loading="lazy" width="80" height="80">
          </div>
          <div class="cart-item__details">
            <h4 class="cart-item__title text-sm text-uppercase letter-spaced">${item.product_title}</h4>
            ${item.variant_title ? `<p class="cart-item__variant text-xs text-secondary">${item.variant_title}</p>` : ''}
            ${item.selling_plan_allocation && item.selling_plan_allocation.selling_plan ? `<p class="cart-item__variant cart-item__plan text-xs text-secondary">${BH.escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
            <div class="cart-item__quantity">
              <button class="cart-item__qty-btn" data-qty-change="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
              <span class="cart-item__qty-value">${item.quantity}</span>
              <button class="cart-item__qty-btn" data-qty-change="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div class="cart-item__price">
            <span>${BH.formatMoney(item.final_line_price)}</span>
            <button class="cart-item__remove text-xs text-secondary" data-qty-change="${item.key}" data-qty="0" aria-label="Remove ${item.title}">Remove</button>
          </div>
        </div>
      `;
    },

    renderEmpty() {
      return `
        <div class="cart-drawer__empty">
          <h3 class="text-uppercase letter-spaced">Your Cart is Empty</h3>
          <p class="text-secondary mt-md">Explore our collection and find something you love.</p>
          <button class="btn btn--primary btn--full mt-lg" onclick="BrewHouse.CartDrawer.close()">Continue Shopping</button>
        </div>
      `;
    },

    bindItemEvents() {
      this.drawer?.querySelectorAll('[data-qty-change]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.qtyChange;
          const qty = parseInt(btn.dataset.qty, 10);
          btn.closest('[data-cart-item]')?.classList.add('cart-item--loading');
          await BH.Cart.change(id, qty);
        });
      });
    },
  };

  /* --- Mega Menu --- */
  BH.MegaMenu = {
    init() {
      const triggers = document.querySelectorAll('[data-mega-trigger]');
      let closeTimeout;

      triggers.forEach((trigger) => {
        const menu = document.querySelector(`[data-mega-menu="${trigger.dataset.megaTrigger}"]`);
        if (!menu) return;

        trigger.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          this.closeAll();
          menu.classList.add('mega-menu--open');
          trigger.classList.add('nav-link--active');
        });

        trigger.addEventListener('mouseleave', (e) => {
          const relatedTarget = e.relatedTarget;
          if (menu.contains(relatedTarget)) return;
          closeTimeout = setTimeout(() => {
            menu.classList.remove('mega-menu--open');
            trigger.classList.remove('nav-link--active');
          }, 200); // 200ms delay bridges the gap between the link and the menu
        });

        menu.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
        });

        menu.addEventListener('mouseleave', (e) => {
          const relatedTarget = e.relatedTarget;
          if (trigger.contains(relatedTarget)) return;
          closeTimeout = setTimeout(() => {
            menu.classList.remove('mega-menu--open');
            trigger.classList.remove('nav-link--active');
          }, 200);
        });
      });
    },

    closeAll() {
      document.querySelectorAll('.mega-menu--open').forEach((m) => m.classList.remove('mega-menu--open'));
      document.querySelectorAll('.nav-link--active').forEach((l) => l.classList.remove('nav-link--active'));
    },
  };

  /* --- Mobile Menu --- */
  BH.MobileMenu = {
    init() {
      const openBtn = document.querySelector('[data-mobile-menu-open]');
      const closeBtn = document.querySelector('[data-mobile-menu-close]');
      const menu = document.querySelector('[data-mobile-menu]');
      const overlay = document.querySelector('[data-overlay]');

      if (!menu) return;

      openBtn?.addEventListener('click', () => {
        menu.classList.add('mobile-menu--open');
        overlay?.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });

      closeBtn?.addEventListener('click', () => this.close(menu, overlay));
      overlay?.addEventListener('click', () => this.close(menu, overlay));
    },

    close(menu, overlay) {
      menu?.classList.remove('mobile-menu--open');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    },
  };

  /* --- Mobile Search (header search icon on phones) --- */
  BH.MobileSearch = {
    init() {
      const toggle = document.querySelector('[data-mobile-search-toggle]');
      const panel = document.querySelector('[data-mobile-search]');
      if (!toggle || !panel) return;
      const input = panel.querySelector('[data-mobile-search-input]');

      const setOpen = (open) => {
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        if (open) input?.focus();
      };

      toggle.addEventListener('click', () => setOpen(panel.hidden));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !panel.hidden) {
          setOpen(false);
          toggle.focus();
        }
      });
    },
  };

  /* --- Accordion --- */
  BH.Accordion = {
    init() {
      document.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const content = trigger.nextElementSibling;
          const isOpen = trigger.classList.contains('accordion--open');

          /* Close all in same group */
          const group = trigger.closest('[data-accordion-group]');
          if (group) {
            group.querySelectorAll('[data-accordion-trigger]').forEach((t) => {
              t.classList.remove('accordion--open');
              t.setAttribute('aria-expanded', 'false');
              t.nextElementSibling.style.maxHeight = null;
            });
          }

          if (!isOpen) {
            trigger.classList.add('accordion--open');
            trigger.setAttribute('aria-expanded', 'true');
            content.style.maxHeight = content.scrollHeight + 'px';
          }
        });
      });
    },
  };

  /* --- Intersection Observer (Fade-in animations) --- */
  BH.AnimateOnScroll = {
    init() {
      const elements = document.querySelectorAll('[data-animate]');
      if (!elements.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      elements.forEach((el) => observer.observe(el));
    },
  };

  /* --- Quick Add to Cart --- */
  BH.QuickAdd = {
    init() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;

        e.preventDefault();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;

        btn.classList.add('btn--loading');
        btn.disabled = true;

        try {
          await BH.Cart.add({ id: parseInt(variantId, 10), quantity: 1 });
          BH.CartDrawer.open();
        } catch (err) {
          console.error('[BrewHouse] Quick add error:', err);
        } finally {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
        }
      });
    },
  };

  /* --- Section Rendering (for dynamic updates) --- */
  BH.renderSection = async function (sectionId) {
    try {
      const url = `${window.location.pathname}?section_id=${sectionId}`;
      const response = await fetch(url);
      const html = await response.text();
      return html;
    } catch (err) {
      console.error('[BrewHouse] Section render error:', err);
      return null;
    }
  };

  /* --- Initialize Everything --- */
  document.addEventListener('DOMContentLoaded', function () {
    BH.initScrollDetection();
    BH.CartDrawer.init();
    BH.MegaMenu.init();
    BH.MobileMenu.init();
    BH.MobileSearch.init();
    BH.Accordion.init();
    BH.AnimateOnScroll.init();
    BH.QuickAdd.init();
  });
})();
